import React, { useEffect, useRef, useState } from 'react';
import { fileToBase64, toYMDLocal } from '../../components/shared';
import { obtenerCatalogoCapacitaciones, subirRegistroCapacitacionEmpleado } from './apiExpediente';
import { nombreFoto, quitarPrefijoDataUrl } from './procesarDocumento';
import { parsearFechaIngreso } from './antiguedad';
import { useEvidencia, InfoEvidencia, useGuardiaSalida, DialogoSalida } from './evidencia';

// Registro de capacitación Mobile (M.2B.3), desde el expediente de UN empleado.
// Misma lógica de negocio que el modal Desktop "Subir Registro Escaneado"
// (POST api/capacitaciones/subir-registro), pero el participante es SIEMPRE el
// empleado del expediente: no hay selector de participantes ni masivo.
// "Guardar y distribuir" = guardar la evidencia en Drive (Registros_Capacitacion),
// agregar la sesión al curso y vincular el curso al empleado.
// Evidencia (cámara / archivo / vista previa / guardia) viene de evidencia.jsx.

const texto = v => String(v == null ? '' : v).trim();

export function etiquetaCapacitacion(cap) {
  const detalle = [texto(cap.tema), texto(cap.duracion)].filter(Boolean).join(' · ');
  return detalle ? `${texto(cap.nombre) || texto(cap.id)} — ${detalle}` : (texto(cap.nombre) || texto(cap.id));
}

export function fechaRegistroValida(valor, hoy = new Date()) {
  const f = parsearFechaIngreso(valor);
  if (!f || !/^\d{4}-\d{2}-\d{2}$/.test(String(valor))) return false;
  return String(valor) <= toYMDLocal(hoy); // yyyy-MM-dd comparable como texto; sin fechas futuras
}

export function RegistroCapacitacion({ idEmpleado, nombreEmpleado, guardiaSalida, onGuardado }) {
  const [abierto, setAbierto] = useState(false);
  const [catalogo, setCatalogo] = useState({ estado: 'inactivo', datos: [] });
  const [intentoCatalogo, setIntentoCatalogo] = useState(0);
  const [capId, setCapId] = useState('');
  const [fecha, setFecha] = useState(() => toYMDLocal(new Date()));
  const [fase, setFase] = useState('editando'); // editando | subiendo | verificando | error | dudoso
  const [aviso, setAviso] = useState(null);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  const ev = useEvidencia({
    nombreFoto: () => nombreFoto(idEmpleado, new Date(), 'Capacitacion'),
    alSeleccionar: () => setAviso(null),
    alError: t => setAviso({ tono: 'error', texto: t })
  });
  const ocupado = fase === 'subiendo' || fase === 'verificando';
  const [salidaPendiente, setSalidaPendiente] = useGuardiaSalida(guardiaSalida, abierto && (ev.evidencia != null || ocupado));

  // Catálogo real (mismo endpoint que Desktop), sólo al abrir el formulario.
  useEffect(() => {
    if (!abierto) return undefined;
    let vigente = true;
    setCatalogo(c => ({ estado: 'cargando', datos: c.datos }));
    obtenerCatalogoCapacitaciones().then(
      datos => { if (vigente) setCatalogo({ estado: 'listo', datos }); },
      () => { if (vigente) setCatalogo(c => ({ estado: 'error', datos: c.datos })); }
    );
    return () => { vigente = false; };
  }, [abierto, intentoCatalogo]);

  const reiniciar = () => {
    ev.descartar();
    setCapId('');
    setFecha(toYMDLocal(new Date()));
    setFase('editando');
  };
  const cerrar = () => { reiniciar(); setAbierto(false); };

  const capSeleccionada = catalogo.datos.find(c => String(c.id) === capId) || null;
  const fechaOk = fechaRegistroValida(fecha);
  const puedeGuardar = !!capSeleccionada && !!ev.evidencia && fechaOk && (fase === 'editando' || fase === 'error');

  const guardar = async () => {
    if (!puedeGuardar) return;
    const cap = capSeleccionada;
    const registrosAntes = Number(cap.registros_count) || 0;
    const nombreCurso = texto(cap.nombre) || texto(cap.id);
    setAviso(null);
    setFase('subiendo');
    try {
      const base64 = quitarPrefijoDataUrl(await fileToBase64(ev.evidencia.blob));
      const r = await subirRegistroCapacitacionEmpleado({
        id_capacitacion: String(cap.id), base64, nombre_archivo: ev.evidencia.nombre, fecha, id_empleado: idEmpleado
      });
      if (!montado.current) return;
      const sinVincular = Array.isArray(r.errores_emp) && r.errores_emp.length > 0;
      cerrar();
      setAviso(sinVincular
        ? { tono: 'info', texto: `El registro se guardó en ${nombreCurso}, pero el sistema no pudo vincularlo a #${idEmpleado}. Revísalo en Desktop antes de repetirlo.` }
        : { tono: 'exito', texto: `Registro guardado en ${nombreCurso}` });
      onGuardado();
    } catch {
      if (!montado.current) return;
      // Respuesta ambigua o fallida: conservar evidencia y datos, y revisar si
      // el curso ya registra una sesión nueva antes de permitir otro intento.
      setFase('verificando');
      let registrosDespues = null;
      try {
        const datos = await obtenerCatalogoCapacitaciones();
        if (!montado.current) return;
        setCatalogo({ estado: 'listo', datos });
        const actual = datos.find(c => String(c.id) === String(cap.id));
        if (actual) registrosDespues = Number(actual.registros_count) || 0;
      } catch { /* sin evidencia: se trata como error */ }
      if (!montado.current) return;
      if (registrosDespues != null && registrosDespues > registrosAntes) {
        setFase('dudoso');
        setAviso({ tono: 'info', texto: `No se pudo confirmar el registro, pero ${nombreCurso} ya muestra un registro nuevo. Revísalo en Desktop antes de volver a subirlo.` });
      } else {
        setFase('error');
        setAviso({ tono: 'error', texto: 'No se pudo confirmar el registro. Tu evidencia sigue aquí; revisa antes de intentar de nuevo.' });
      }
    }
  };

  const avisoUI = aviso && (
    <p className={`m-carga-aviso m-carga-aviso-${aviso.tono}`} role={aviso.tono === 'error' ? 'alert' : 'status'}>
      <i className={`ti ${aviso.tono === 'exito' ? 'ti-circle-check' : aviso.tono === 'info' ? 'ti-info-circle' : 'ti-alert-triangle'}`} aria-hidden="true"></i>
      <span>{aviso.texto}</span>
    </p>
  );

  if (!abierto) {
    return (
      <div className="m-carga">
        <button type="button" className="m-carga-accion m-registro-abrir" onClick={() => { setAviso(null); setAbierto(true); }}>
          <i className="ti ti-plus" aria-hidden="true"></i> Subir registro
        </button>
        {avisoUI}
      </div>
    );
  }

  return (
    <section className="m-carga m-carga-panel m-registro-form" aria-labelledby="m-registro-titulo" aria-busy={ocupado}>
      <h2 id="m-registro-titulo" className="m-registro-titulo">Registro de capacitación</h2>

      <label className="m-campo">
        <span className="m-campo-etiqueta">Capacitación <span aria-hidden="true">*</span></span>
        {catalogo.estado === 'error' ? (
          <span className="m-campo-error">
            No se pudo cargar el catálogo.{' '}
            <button type="button" className="m-exp-reintentar" onClick={() => setIntentoCatalogo(i => i + 1)}>Reintentar</button>
          </span>
        ) : (
          <select
            className="m-campo-control"
            value={capId}
            onChange={e => setCapId(e.target.value)}
            disabled={ocupado || catalogo.estado !== 'listo'}
            required
          >
            <option value="">{catalogo.estado === 'listo' ? '— Seleccionar —' : 'Cargando capacitaciones…'}</option>
            {catalogo.datos.map(c => <option key={c.id} value={String(c.id)}>{etiquetaCapacitacion(c)}</option>)}
          </select>
        )}
        {catalogo.estado === 'listo' && catalogo.datos.length === 0 && (
          <span className="m-campo-ayuda">No hay capacitaciones. Créalas en SIGRH Desktop.</span>
        )}
        {capSeleccionada && [texto(capSeleccionada.expositor), texto(capSeleccionada.lugar)].some(Boolean) && (
          <span className="m-campo-ayuda">{[texto(capSeleccionada.expositor), texto(capSeleccionada.lugar)].filter(Boolean).join(' · ')}</span>
        )}
      </label>

      <label className="m-campo">
        <span className="m-campo-etiqueta">Fecha del registro</span>
        <input
          className="m-campo-control"
          type="date"
          value={fecha}
          max={toYMDLocal(new Date())}
          onChange={e => setFecha(e.target.value)}
          disabled={ocupado}
        />
        {!fechaOk && <span className="m-campo-error">Indica una fecha válida que no sea futura.</span>}
      </label>

      <div className="m-campo">
        <span className="m-campo-etiqueta">Participante</span>
        <p className="m-registro-participante"><strong>{nombreEmpleado}</strong> · #{idEmpleado}</p>
        <span className="m-campo-ayuda">Se registrará únicamente a este empleado.</span>
      </div>

      <div className="m-campo">
        <span className="m-campo-etiqueta">Evidencia <span aria-hidden="true">*</span></span>
        {ev.evidencia ? (
          <>
            <InfoEvidencia evidencia={ev.evidencia} />
            {!ocupado && fase !== 'dudoso' && (
              <button type="button" className="m-carga-cancelar m-registro-quitar" onClick={ev.descartar}>Quitar evidencia</button>
            )}
          </>
        ) : ev.procesando ? (
          <p className="m-carga-estado" role="status"><i className="ti ti-loader-2 m-girando" aria-hidden="true"></i> Preparando foto…</p>
        ) : (
          <div className="m-carga-acciones">
            <button type="button" className="m-carga-accion" onClick={() => { setAviso(null); ev.abrirCamara(); }} disabled={ocupado}>
              <i className="ti ti-camera" aria-hidden="true"></i> Tomar foto
            </button>
            <button type="button" className="m-carga-accion" onClick={() => { setAviso(null); ev.abrirArchivo(); }} disabled={ocupado}>
              <i className="ti ti-upload" aria-hidden="true"></i> Subir archivo
            </button>
          </div>
        )}
        {ev.entradas}
      </div>

      {avisoUI}

      {ocupado && (
        <p className="m-carga-estado" role="status">
          <i className="ti ti-loader-2 m-girando" aria-hidden="true"></i> {fase === 'subiendo' ? 'Guardando registro…' : 'Revisando capacitación…'}
        </p>
      )}

      {fase === 'dudoso' ? (
        <div className="m-carga-botones">
          <button type="button" className="m-btn m-btn-primario" onClick={() => { cerrar(); }}>Entendido</button>
        </div>
      ) : !ocupado && (
        <div className="m-carga-botones">
          <button type="button" className="m-btn m-btn-secundario" onClick={() => { setAviso(null); cerrar(); }}>Cancelar</button>
          <button type="button" className="m-btn m-btn-primario" onClick={guardar} disabled={!puedeGuardar}>
            {fase === 'error' ? 'Intentar de nuevo' : 'Guardar y distribuir'}
          </button>
        </div>
      )}

      {salidaPendiente && (
        <DialogoSalida
          subiendo={ocupado}
          textoPendiente="Tienes un registro de capacitación con evidencia sin guardar. Si sales, se descartará."
          onSeguir={() => setSalidaPendiente(null)}
          onSalir={() => {
            const continuar = salidaPendiente;
            if (guardiaSalida) guardiaSalida.current = null;
            setSalidaPendiente(null);
            cerrar();
            continuar();
          }}
        />
      )}
    </section>
  );
}
